import { getProduct } from '@commerce/api/operations'

import { useQuery, useReactiveVar } from '@apollo/client'
import {
  customerAccessTokenVar,
  storefrontCustomerAccessTokenVar,
} from '@commerce/client'
import type {
  ContextualizeProduct,
  ContextualizeShopifyProduct,
  Product,
} from '@commerce/types/product'
import { ProductMetafields } from '@commerce/types/product'
import {
  getCurrencyCountryCode,
  getCustomerQuery,
  getLanguage,
  getProductsPricesByIds,
  normalizeContextualizeProduct,
} from '@commerce/utils'
import { Breadcrumbs, Layout, Loader } from '@components/common'
import { SauceGallery, SauceProductGallery } from '@components/common/Sauce'
import { ProductView } from '@components/product'
import { Container } from '@components/ui'
import productBuildPaths from '@config/productBuildPaths.json'
import { getColourGroupFromTags } from '@lib/colors'
import getGlobalStaticProps from '@lib/globalStaticProps'
import {
  COLOUR_CARD_OPTION_LABEL,
  GARDEN_POT_PAINT_OPTION_LABEL,
  HIDDEN_TAG,
  HIDE_SAUCE_TAG,
  PAINT_15_LITRE_OPTION_LABEL,
  PAINT_OPTION_LABEL,
  PRODUCT_TYPE_COLOUR,
  PRODUCT_TYPE_TOOLS,
  SAMPLE_POT_OPTION_LABEL,
  findBundleInfoFromMetafield,
  orderMetafieldByKey,
} from '@lib/helpers/general'
import { getConfigByLocale } from '@lib/locale'
import { rPDWCPD } from '@lib/products'
import { SliceZone } from '@prismicio/react'
import { components } from '@slices/index'
import type { GetStaticPropsContext, InferGetStaticPropsType } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { createClient, findPrismicUidFromTags, getDocByUid } from 'prismicio'
import { Fragment } from 'react'

type ProductPath = { params: { slug: string }; locale: string }

export async function getStaticProps({
  params,
  locale,
  previewData,
}: GetStaticPropsContext<{ slug: string }>) {
  const productPromise = getProduct(locale, params!.slug, 'no-cache')
  const { product }: { product: Product } = await productPromise
  if (!product || product?.tags?.includes(HIDDEN_TAG)) {
    return { notFound: true }
  }
  const translations = await serverSideTranslations(locale ?? 'en-au', [
    'common',
    'product',
    'cart',
  ])
  let productSiblings: Product[] = []

  const metafieldsWithOrder = orderMetafieldByKey(product?.metafields!)

  const switchLabel = (index: number) => {
    switch (index) {
      case 0: {
        return COLOUR_CARD_OPTION_LABEL
      }
      case 1: {
        return SAMPLE_POT_OPTION_LABEL
      }
      case 2: {
        return GARDEN_POT_PAINT_OPTION_LABEL
      }
      case 3: {
        return PAINT_OPTION_LABEL
      }
      case 4: {
        return PAINT_15_LITRE_OPTION_LABEL
      }
    }
  }

  const getProductSiblings: any = metafieldsWithOrder?.map(
    async (v: ProductMetafields, index: number) => {
      try {
        await getProduct(locale, v.value, 'no-cache').then(
          (res) => (productSiblings[index] = res.product)
        )
        productSiblings = productSiblings.map(
          (sibling: any, index: number) => ({
            ...sibling,
            siblingOptionLabel: switchLabel(index),
          })
        )
      } catch (err) {
        console.log(err)
      }
    }
  )

  if (product.productType === PRODUCT_TYPE_COLOUR)
    await Promise.all(getProductSiblings)

  const client = createClient({ previewData })
  const globalProps = await getGlobalStaticProps(client, locale)
  let doc = null
  try {
    const prismicDocUId = findPrismicUidFromTags(product.tags)
    if (prismicDocUId) {
      doc = await getDocByUid('colour-pdp', prismicDocUId, client, locale)
    }
  } catch (e) {}
  let colourProductContent = null
  try {
    colourProductContent = await getDocByUid(
      'colour-product-content',
      'colour-product-content',
      client,
      locale
    )
  } catch (e) {
    console.log(e)
  }

  return {
    props: {
      ...globalProps,
      ...translations,
      pages: [],
      product,
      productSiblings: productSiblings.filter((p) => p),
      colourProductContent,
      categories: [],
      doc,
    },
    revalidate: 86400, // Refresh every 24 hours
  }
}

export async function getStaticPaths() {
  if (process?.env?.NODE_ENV === 'production') {
    return {
      paths: [],
      fallback: true,
    }
  }
  const paths: ProductPath[] = productBuildPaths

  return {
    paths,
    fallback: true,
  }
}
export default function Slug({
  product,
  doc,
  productSiblings,
  colourProductContent,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const router = useRouter()
  const { locale } = useRouter()
  const { data: prismicDocData }: any = doc || {}
  const p: Product = product

  // get customer specific data

  const customerAccessToken = useReactiveVar(customerAccessTokenVar)
  const storefrontCustomerAccessToken = useReactiveVar(
    storefrontCustomerAccessTokenVar
  )

  // get customer data
  const { data: customerData, loading: customerLoading } = useQuery(
    getCustomerQuery,
    {
      context: { locale, customerAccessToken, useCustomerApi: true },
      skip: !(customerAccessToken && customerAccessToken.length > 0),
    }
  )

  const allProductIds = [product?.id].concat(productSiblings?.map((s) => s.id))
  // get customer location id to fetch pricing
  const companyLocationId =
    customerData?.customer?.companyContacts?.edges?.[0]?.node?.company
      ?.locations?.edges?.[0]?.node?.id

  // fetch custom prices on base of customer company id and product ids
  const { data: customPrices, loading: customPricesLoading } = useQuery(
    getProductsPricesByIds,
    {
      context: { locale },
      variables: {
        country: String(getCurrencyCountryCode(locale)),
        language: String(getLanguage(locale)),
        buyer: {
          customerAccessToken: storefrontCustomerAccessToken,
          companyLocationId,
        },
        query: allProductIds
          .map((i) => i?.replace('gid://shopify/Product/', ''))
          .join(' OR '),
      },
      skip: !(
        storefrontCustomerAccessToken &&
        storefrontCustomerAccessToken.length > 0 &&
        companyLocationId &&
        product?.id &&
        allProductIds?.length > 0
      ),
    }
  )

  let firstCrum = {
    label: 'colour-lab',
    link: '/collection/colour-lab',
  }
  if (p?.productType === PRODUCT_TYPE_TOOLS)
    firstCrum = {
      label: 'tools-supplies',
      link: '/collection/tools-and-supplies',
    }
  const crumbs = [
    firstCrum,
    {
      label: p?.name,
      link: router.asPath,
    },
  ]
  if (router.isFallback) return <Loader title="product-loader" />

  // if (!data || !product) {
  //   return <Error code={404} />
  // }
  const bundleInfo = findBundleInfoFromMetafield(product.metafields)
  const isBundleProduct = !!bundleInfo
  const showBreadCrumb =
    p?.productType === PRODUCT_TYPE_COLOUR ||
    p?.productType === PRODUCT_TYPE_TOOLS ||
    isBundleProduct
  const colourGroup = getColourGroupFromTags(p?.tags)
  const sauceId = getConfigByLocale(router.locale!, 'sauceId')
  const displaySauce = !p?.tags?.includes(HIDE_SAUCE_TAG)

  // replace customer pricing data in the product
  let productWithContxualisedPrices: ContextualizeProduct[] = []
  if (!customPricesLoading && customPrices?.products) {
    productWithContxualisedPrices = customPrices?.products?.edges?.map(
      ({ node }: { node: ContextualizeShopifyProduct }) =>
        normalizeContextualizeProduct(node)
    )

    const mainProduct = productWithContxualisedPrices.find(
      (p) => p.id === product.id
    )
    if (mainProduct) {
      product = rPDWCPD(product, mainProduct)
    }

    productSiblings = productSiblings.map((p) => {
      const cp = productWithContxualisedPrices.find((mp) => mp.id === p.id)
      if (cp) p = rPDWCPD(p, cp)
      return p
    })
  }

  return (
    <>
      <Head>
        <meta name="sauce-product-handle" content={p.handle} />
      </Head>
      {showBreadCrumb && (
        <div className="bg-brand-light-grey">
          <Container className="py-2 md:pt-6 md:pb-4">
            <Breadcrumbs crumbs={crumbs} />
          </Container>
        </div>
      )}
      <div className="lg:pb-8">
        <ProductView
          product={product}
          productSiblings={productSiblings}
          prismicContent={colourProductContent}
          key={product.id}
        />
      </div>

      {sauceId && displaySauce && (
        <Fragment key={`${p.handle}-${router.locale}`}>
          <div className="lg:pt-8">
            <div className="bg-brand-light-grey pb-8">
              <Container>
                <SauceProductGallery handle={p.handle!} />
              </Container>
            </div>
            {colourGroup && (
              <div className="bg-white pt-16 pb-8">
                <Container>
                  <SauceGallery
                    galleryHandle={colourGroup!}
                    handle={p.handle!}
                  />
                </Container>
              </div>
            )}
          </div>
        </Fragment>
      )}
      <SliceZone
        slices={prismicDocData?.slices}
        components={components}
        context={product}
      />
      <style jsx global>
        {`
          .snptwdgt__headr {
            padding-bottom: 32px !important;
          }
          .snptwdgt__headng {
            text-align: left !important;
          }
          .snptwdgt__subheadng {
            text-align: left !important;
          }
        `}
      </style>
    </>
  )
}

Slug.Layout = Layout
